import axios from 'axios';
import authAxios from '../../lib/authAxios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Messages } from 'primereact/messages';
import { getErrorMessage } from '../../../utils/errors';
import { showErrorMessage, showSuccessMessage } from '../../utils/messages';
import { ProgressSpinner, Checkbox } from '../../../components/PrimereactComponents';
import { ButtonAdd, ButtonDownload } from '../../components/Buttons';
import { DatabaseTable } from './components/DatabaseTable';
import { DatabaseDialog } from './components/DatabaseDialog';
import { BackupDialog } from './components/BackupDialog';
import { ExportDialog } from './components/ExportDialog';
import { ImportDialog } from './components/ImportDialog';

export default function Databases() {

    const [databases, setDatabases] = useState(null);
    const [databaseTypeOptions, setDatabaseTypeOptions] = useState([]);
    
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    
    const [showDeletedDatabases, setShowDeletedDatabases] = useState(false);
    
    const [selectedDatabase, setSelectedDatabase] = useState(null);
    const [checkedDatabases, setCheckedDatabases] = useState(new Set());
    const [selectAllCheckbox, setSelectAllCheckbox] = useState(false);
    
    const [showDatabaseDialog, setShowDatabaseDialog] = useState(false);
    const refDatabaseDialogMsgs = useRef(null);
    const refDatabaseDialogForm = useRef(null);
    
    const [showExportDialog, setShowExportDialog] = useState(false);
    const refExportDialogMsgs = useRef(null);
    const refExportDialogForm = useRef(null);

    const [showImportDialog, setShowImportDialog] = useState(false);
    const refImportDialogMsgs = useRef(null);
    const refImportDialogForm = useRef(null);

    const [showBackupDialog, setShowBackupDialog] = useState(false);
    const refBackupDialogMsgs = useRef(null);
    const [backups, setBackups] = useState(null);

    const navigate = useNavigate();

    const msgs = useRef(null);

    const loadDatabases = useCallback(async () => {
        const response = await authAxios.get('admin/database');
        setDatabases(response.data);
    }, []);

    const loadDatabaseTypes = useCallback(async () => {
        const response = await authAxios.get('admin/database-type');
        setDatabaseTypeOptions(
            response.data.map((databaseType) => ({
                label: databaseType.name,
                value: databaseType.id
            }))
        );
    }, []);

    const loadBackups = useCallback(async (databaseId) => {
        const response = await authAxios.get(`/admin/database/${databaseId}/backup`);
        setBackups(response.data);
        return response.data;
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                await Promise.all([loadDatabases(), loadDatabaseTypes()]);
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se načíst databáze');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [loadDatabases, loadDatabaseTypes]);

    const downloadFile = (apiResponse, fileName) => {
        // Might be better to install the FileSaver library to handle downloads
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(new Blob([apiResponse.data]));
        a.download = fileName
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(a.href);
    }

    const hideDatabaseDialog = () => {
        setShowDatabaseDialog(false);
        setSelectedDatabase(null);
    };

    const handleClickEditDatabase = (rowData) => {
        setSelectedDatabase(rowData);
        setShowDatabaseDialog(true);
    };

    const handleEditDatabase = async (data) => {
        setProcessing(true);
        const requestData = {
            name: data.name
        };
        try {
            await authAxios.put(`/admin/database/${selectedDatabase.id}`, requestData);
            showSuccessMessage(
                msgs,
                `Databáze byla úspěšně upravena`
            );

            // Update database table with new DB name
            const updatedDatabase = {...selectedDatabase, name: data.name};
            setDatabases((prevDatabases) =>
                prevDatabases.map((db) =>
                    db.id === selectedDatabase.id ? updatedDatabase : db
                )
            );

            hideDatabaseDialog();
            refDatabaseDialogForm?.current?.reset();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refDatabaseDialogMsgs,
                errorMessage ?? 'Nepodařilo se upravit databázi',
                { replace: true }
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleClickAddDatabase = () => {
        setShowDatabaseDialog(true);
    };

    const handleAddDatabase = async (data) => {
        setProcessing(true);
        const requestData = {
            db_type: data.db_type,
            name: data.name
        };
        try {
            await authAxios.post('/admin/database', requestData);
            showSuccessMessage(
                msgs,
                `Databáze ${data.name} byla úspěšně vytvořena`
            );
            loadDatabases();
            hideDatabaseDialog();
            refDatabaseDialogForm?.current?.reset();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refDatabaseDialogMsgs,
                errorMessage ?? 'Nepodařilo se vytvořit novou databázi',
                { replace: true }
            );
        } finally {
            setProcessing(false);
        }
    };

    const handleDeleteDatabase = async (nameConfirmation) => {
        setProcessing(true);
        if (nameConfirmation !== selectedDatabase.name) {
            showErrorMessage(refDatabaseDialogMsgs,
                'Zadané jméno databáze ke smazání se neshoduje',
                { replace: true }
            );
            setProcessing(false);
            return;
        }

        try {
            await authAxios.delete(`/admin/database/${selectedDatabase.id}`);
            showSuccessMessage(
                msgs,
                `Databáze ${nameConfirmation} byla úspěšně smazána`
            );

            // Set the corresponding DB's 'deleted' attribute to true
            setDatabases((prevDatabases) =>
                prevDatabases.map((db) =>
                    db.id === selectedDatabase.id ? { ...db, deleted: true } : db
                )
            );

            hideDatabaseDialog();
            refDatabaseDialogForm?.current?.reset();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(
                refDatabaseDialogMsgs,
                errorMessage ?? 'Nepodařilo se smazat databázi',
                { replace: true }
            );
        } finally {
            setProcessing(false);
        }
    }

    const handleClickExportDatabaseOne = (rowData) => {
        setSelectedDatabase(rowData);
        setShowExportDialog(true);
    };

    const handleClickExportDatabaseList = () => {
        setShowExportDialog(true);
    };

    const hideExportDialog = () => {
        setShowExportDialog(false);
        setSelectedDatabase(null);
    };

    const handleExport = async (url, requestData) => {
        setProcessing(true);

        try {
            const response = await authAxios.post(url, requestData, { responseType: 'blob' });
            const fileName = selectedDatabase ? `${selectedDatabase.name}.sql` : 'db-bulk-export.sql';
            downloadFile(response, fileName);
            hideExportDialog();
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refExportDialogMsgs, errorMessage ?? 'Nepodařilo se exportovat databázi', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    const handleExportOneDialog = async (data) => {
        const requestData = {
            create_db: data.create_db,
            drop_db: data.drop_db,
            drop_table: data.drop_table,
            include_data: data.include_data,
        };
        await handleExport(`/admin/database/${selectedDatabase.id}/export`, requestData);
    };

    const handleExportListDialog = async (data) => {
        const db_ids = [];
        for (const db of checkedDatabases) {
            db_ids.push(db.id);
        }
        const requestData = {
            db_ids,
            create_db: data.create_db,
            drop_db: data.drop_db,
            drop_table: data.drop_table,
            include_data: data.include_data,
        };
        await handleExport(`/admin/database/export`, requestData);
    };

    const handleClickImportDump = () => {
        setShowImportDialog(true);
    }

    const hideImportDialog = () => {
        setShowImportDialog(false);
    }

    const handleImportDump = async (data) => {
        if (!data.sql_dump) return;
        setProcessing(true);
        try {
            const formData = new FormData();
            formData.append('sql_dump', data.sql_dump);
            await authAxios.post('/admin/database/import', formData);
            
            showSuccessMessage(
                msgs,
                'Soubor byl úspěšně importován'
            );
            loadDatabases();
            refImportDialogForm?.current?.resetFile();
            hideImportDialog();

        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refImportDialogMsgs, errorMessage ?? 'Nepodařilo se importovat soubor', { replace: true });
        } finally {
            setProcessing(false);
        }
    }

    const handleClickShowBackups = async (rowData) => {
        setProcessing(true);
        try {
            await loadBackups(rowData.id);
            setSelectedDatabase(rowData);
            setShowBackupDialog(true);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(msgs, errorMessage ?? 'Nepodařilo se načíst seznam záloh', { replace: true });
            setSelectedDatabase(null);
        } finally {
            setProcessing(false);
        }
    }

    const hideBackupDialog = () => {
        setShowBackupDialog(false);
        setSelectedDatabase(null);
        setBackups(null);
    }

    const handleDownloadBackup = async (backupData) => {
        setProcessing(true);
        try {
            const response = await authAxios.get(`/admin/database/${selectedDatabase.id}/backup/${backupData.id}`)
            const [day, month, year] = backupData.backup_timestamp.split(" ")[0].split(".");
            const fileName = `db-${selectedDatabase.name}_${year}-${month}-${day}.sql`;
            downloadFile(response, fileName);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refBackupDialogMsgs, errorMessage ?? 'Nepodařilo se stáhnout zálohu', { replace: true });
        } finally {
            setProcessing(false);
        }
    }

    const handleCreateBackup = async () => {
        setProcessing(true);
        try {
            await authAxios.post(`/admin/database/${selectedDatabase.id}/backup`);
            showSuccessMessage(refBackupDialogMsgs, `Záloha byla úspěšně vytvořena`);

            const updatedBackups = await loadBackups(selectedDatabase.id);
            const newBackup = updatedBackups[updatedBackups.length - 1];

            setDatabases((prevDatabases) =>
                prevDatabases.map((db) =>
                    db.id === selectedDatabase.id ? { ...db, last_backup: newBackup.backup_timestamp } : db
                )
            );
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showErrorMessage(refBackupDialogMsgs, errorMessage ?? 'Nepodařilo se vytvořit zálohu', { replace: true });
        } finally {
            setProcessing(false);
        }
    };

    const isRowSelected = (rowData) => {
        return checkedDatabases.has(rowData);
    };

    const handleRowCheckboxChange = (rowData) => {
        setCheckedDatabases(prevCheckedDatabases => {
            const newCheckedDatabases = new Set(prevCheckedDatabases);
            if (newCheckedDatabases.has(rowData)) {
                newCheckedDatabases.delete(rowData);
            } else {
                newCheckedDatabases.add(rowData);
            }
            return newCheckedDatabases;
        });
    };

    const handleHeaderCheckboxChange = () => {
        const newState = !selectAllCheckbox;
        if (newState) {
            setCheckedDatabases(new Set(databases));
        } else {
            setCheckedDatabases(new Set());
        }
        setSelectAllCheckbox(newState);
    };

    const handleShowDeletedCheckboxChange = () => {
        setShowDeletedDatabases(!showDeletedDatabases);
    }

    return (
        <>
            <h1>Databáze</h1>

            <div className='page__buttons'>
                <ButtonAdd
                    label='Přidat databázi'
                    onClick={handleClickAddDatabase}
                    disabled={processing}
                />

                <ButtonAdd
                    label='Importovat SQL dump'
                    onClick={handleClickImportDump}
                    disabled={processing}
                />
                {(processing) && <ProgressSpinner />}

                <div className='show-all-checkbox-container'>
                    <label>
                        Zobrazit smazané databáze
                        <Checkbox
                            name='showDeletedCheckbox'
                            checked={showDeletedDatabases}
                            onChange={handleShowDeletedCheckboxChange}
                        />
                    </label>
                </div>
            </div>

            <Messages ref={msgs} className='page__messages' />

            <DatabaseTable
                databases={databases ?
                    (
                        showDeletedDatabases ?
                        databases : databases.filter(
                            database => database.deleted === false
                        )
                    )
                    : null
                }
                loading={loading}
                onClickExportDatabase={handleClickExportDatabaseOne}
                onClickEditDatabase={handleClickEditDatabase}
                onClickShowBackups={handleClickShowBackups}
                onChangeRowCheckbox={handleRowCheckboxChange}
                onChangeHeaderCheckbox={handleHeaderCheckboxChange}
                rowCheckboxSelected={isRowSelected}
                headerCheckboxSelected={selectAllCheckbox}
                disabled={processing}
            />

            <div className={`floating-button ${checkedDatabases.size <= 0 ? 'hide' : ''}`}>
                <ButtonDownload
                    type='submit'
                    label='Exportovat databáze'
                    onClick={handleClickExportDatabaseList}
                    disabled={processing}
                />
            </div>

            <DatabaseDialog
                isVisible={showDatabaseDialog}
                onHide={hideDatabaseDialog}
                onSubmitAdd={handleAddDatabase}
                onSubmitEdit={handleEditDatabase}
                onSubmitDelete={handleDeleteDatabase}
                databaseTypeOptions={databaseTypeOptions}
                editDatabase={selectedDatabase}
                msgs={refDatabaseDialogMsgs}
                ref={refDatabaseDialogForm}
            />

            <ExportDialog
                isVisible={showExportDialog}
                onHide={hideExportDialog}
                onSubmitOne={handleExportOneDialog}
                onSubmitList={handleExportListDialog}
                databaseOne={selectedDatabase}
                databaseList={checkedDatabases}
                msgs={refExportDialogMsgs}
                ref={refExportDialogForm}
            />

            <ImportDialog
                isVisible={showImportDialog}
                onHide={hideImportDialog}
                onSubmit={handleImportDump}
                msgs={refImportDialogMsgs}
                ref={refImportDialogForm}
            />

            <BackupDialog
                isVisible={showBackupDialog}
                onHide={hideBackupDialog}
                onClickDownloadBackup={handleDownloadBackup}
                onClickCreateBackup={handleCreateBackup}
                database={selectedDatabase}
                backups={backups}
                msgs={refBackupDialogMsgs}
            />
            
        </>
    );
}
